<template>
	<Loading v-if="store.isLoading" />

	<section
		v-if="!data.posts || data.posts.length === 0"
		class="h-[500px]"
	/>

	<Row class="pb-0 bg-white">
		<div class="container xl:container gap-[80px]">
			<h2
				class="text-[32px] lg:text-[44px] leading-none lg:leading-tight border-l-[8px] pl-[16px] border-primary-500 leading-none mb-[16px] md:mb-[32px] ml-auto"
			>
				<b>Tag:</b>
				{{ data.name }}
			</h2>
		</div>
	</Row>

	<Row>
		<div class="xl:container gap-[80px]">
			<section
				class="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-[32px] px-[16px] md:px-[32px] xl:px-0"
			>
				<NuxtLink
					v-for="post in data.posts"
					:key="post.slug"
					:to="`/blog/${post.slug}/`"
					class="post-tile"
				>
					<div
						class="post-image"
						:style="`background-image:url(${
							post.featuredImage ? post.featuredImage.node.sourceUrl : ''
						})`"
					/>
					<div class="post-content">
						<div class="post-metadata mb-[8px]">
							<span v-if="post.categories.nodes[0] && post.categories.nodes[0].name">
								{{ truncateText(post.categories.nodes[0].name, 15) }}
							</span>
							<span>{{ calculateReadingTime(post.content) }} min read</span>
						</div>
						<h3 v-html="reponsiveTruncateText(post.title)" />
					</div>
				</NuxtLink>
			</section>
		</div>
	</Row>
	<Head>
		<Title>{{ data.name }} - Timothy Sykes</Title>
	</Head>
</template>

<script setup>
store.toggleLoading();

const { data } = await useAsyncData(
	"tags_posts",
	async ({ $config, $router }) => {
		try {
			const response = await fetchTagPostsList(
				`${$config.public.hostname}graphql`,
				$router.currentRoute._rawValue.params.slug[0]
			);

			const { tags } = response.data;

			if (tags.nodes.length === 0) {
				return [];
			}

			const { slug, name, posts } = tags.nodes[0];

			return {
				name,
				slug,
				posts: posts.nodes
			};
		} catch (e) {
			// eslint-disable-next-line no-console
			console.error(e);
		}
	}
);

if (!data.value) {
	throw createError({
		statusCode: 404,
		statusMessage: "Page Not Found",
		fatal: true
	});
}

onMounted(() => {
	if (process.client) {
		if (data.value.posts) {
			store.toggleLoading();
		}
	}
});

const reponsiveTruncateText = (text) => {
	if (process.client) {
		if (window.innerWidth > 1280) {
			return truncateText(text, 49);
		}

		return truncateText(text, 62);
	} else {
		return truncateText(text, 49);
	}
};

useHead({
	meta: [{ hid: "robots", name: "robots", content: NOINDEX_ROBOTS_CONTENT }]
});
</script>

<style lang="scss" scoped>
@import "@assets/utils.scss";

.post-tile {
	border-radius: 8px;
	overflow: hidden;
	filter: drop-shadow(0px 7px 20px rgba(0, 0, 0, 0.1));
}

.post-image {
	height: 200px;
	display: flex;
	align-items: center;
	justify-content: flex-start;
	background-color: $primary-300;
	overflow: hidden;
	background-size: cover;
	background-position: center;
}

.post-metadata {
	display: flex;

	span {
		line-height: 1;
		font-family: Rubik;
		font-size: 18x;
		font-style: normal;
		font-weight: 400;
		color: $neutral-400;
	}

	span:first-child {
		padding-right: 8px;
		border-right: none !important;
		padding-left: 0 !important;
	}

	span:nth-child(2) {
		padding-left: 8px;
		border-left: 1px solid $neutral-400;
	}
}

.post-content {
	padding: 8px 16px 16px;
	background-color: white;

	@include breakpoint(md) {
		min-height: 92px;
	}
}

.post-content h3 {
	color: $neutral-800;
	/* Heading/H6 */
	font-family: Fira Sans;
	font-size: 18px;
	font-weight: 600;
	line-height: 120%;
	/* 21.6px */
}
</style>
